import React from 'react';
import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getPostBySlug, getRelatedPosts } from '../data/virtualStagingBlogPosts';

const VirtualStagingBlogPost = ({ slug }) => {
  const post = getPostBySlug(slug);

  if (!post) {
    // Redirect to blog list
    window.location.href = '/staging-blog';
    return null;
  }

  const relatedPosts = getRelatedPosts(post, 3);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, url });
      } catch (e) {
        // User cancelled
      }
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <style>{`
        .prose {
          color: #d1d5db;
          line-height: 1.8;
        }
        .prose h2 {
          color: #ffffff;
          font-size: 1.75rem;
          font-weight: 700;
          margin-top: 2.5rem;
          margin-bottom: 1rem;
        }
        .prose h3 {
          color: #ffffff;
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
        }
        .prose p {
          margin-bottom: 1.25rem;
        }
        .prose strong {
          color: #ffffff;
          font-weight: 600;
        }
        .prose ul, .prose ol {
          margin-bottom: 1.25rem;
          padding-left: 1.5rem;
        }
        .prose li {
          margin-bottom: 0.5rem;
        }
        .prose a {
          color: #10b981;
          text-decoration: underline;
        }
        .prose a:hover {
          color: #059669;
        }
        .prose blockquote {
          border-left: 4px solid #10b981;
          padding-left: 1rem;
          margin: 1.5rem 0;
          font-style: italic;
          color: #9ca3af;
        }
        .prose hr {
          border-color: #374151;
          margin: 2rem 0;
        }
        .prose table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.5rem 0;
        }
        .prose th, .prose td {
          border: 1px solid #374151;
          padding: 0.75rem 1rem;
          text-align: left;
        }
        .prose th {
          background-color: #1f2937;
          color: #ffffff;
          font-weight: 600;
        }
        .prose code {
          background-color: #1f2937;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.875rem;
        }
        .prose pre {
          background-color: #1f2937;
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 1.5rem 0;
        }
        .prose pre code {
          padding: 0;
          background: none;
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-slate-950/90 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <a href="/staging-blog" className="flex items-center gap-2 text-white hover:text-emerald-400 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Blog</span>
          </a>
          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <Share2 className="w-4 h-4" />
            <span className="text-sm">Share</span>
          </button>
        </div>
      </nav>

      {/* Article */}
      <article className="pt-24 pb-16">
        {/* Hero Image */}
        <div className="w-full h-64 md:h-96 relative">
          <img
            src={post.featuredImage}
            alt={post.imageAlt}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 to-transparent" />
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-6 -mt-16 relative">
          {/* Meta */}
          <div className="mb-6">
            <span className="inline-block px-3 py-1 bg-emerald-500/20 text-emerald-400 text-sm font-medium rounded-full mb-4">
              {post.category}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {post.title}
            </h1>
            <div className="flex items-center gap-6 text-slate-400">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(post.date)}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {post.readTime} min read
              </span>
            </div>
          </div>

          {/* Body */}
          <div className="prose max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {post.content}
            </ReactMarkdown>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-emerald-900/40 to-emerald-800/40 rounded-2xl border border-emerald-500/30 text-center">
            <h3 className="text-2xl font-bold text-white mb-3">Ready to Transform Your Listings?</h3>
            <p className="text-slate-300 mb-6">Try our AI-powered virtual staging and see the difference for yourself.</p>
            <a
              href="/virtual-staging"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-xl transition-colors"
            >
              Start Staging Now
            </a>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 px-6 bg-slate-900/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Related Articles</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {relatedPosts.map((relatedPost) => (
                <a
                  key={relatedPost.id}
                  href={`/staging-blog/${relatedPost.slug}`}
                  className="bg-slate-900 rounded-xl overflow-hidden border border-slate-800 hover:border-emerald-500/50 transition-all"
                >
                  <img
                    src={relatedPost.featuredImage}
                    alt={relatedPost.imageAlt}
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4">
                    <span className="text-emerald-400 text-xs uppercase tracking-wider">
                      {relatedPost.category}
                    </span>
                    <h3 className="text-white font-semibold mt-2 line-clamp-2">
                      {relatedPost.title}
                    </h3>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default VirtualStagingBlogPost;
